import { useMemo, useState } from 'react'
import { DateRange } from 'react-day-picker'
import dayjs from 'dayjs'
import {
  useGetOrgCreditAnalytics,
  useGetOrgServerAnalytics,
  useGetOrgStorageAnalytics,
} from '@/services/home'
import { CalendarPopover } from '@/components/calendar-popover'
import { LoadingMessage } from '@/components/loading-message'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Table,
  TableCell,
  TableFooter,
  TableRow,
} from '@/components/ui/table-complex'
import { formatCurrency, getTimeData } from '@/lib/utils'
import { useGetOrgUserProfile } from '@/services/auth'
import { useActiveOrgId } from '@/hooks/useActiveOrgId'
import { getFormattedUnixDate } from '@/helpers/time'
import { AnalyticsUsersTable } from './AnalyticsUsers'
import { AnalyticsOverviewTable } from './AnalyticsOverview'

type AnalyticsType = 'overview' | 'server' | 'storage'

export const DashboardAnalyticsTab = () => {
  const { orgId } = useActiveOrgId()
  const { data: org, isLoading: isOrgLoading } = useGetOrgUserProfile(orgId)

  const [range, setRange] = useState<DateRange>({
    from: dayjs().subtract(1, 'week').toDate(),
    to: dayjs().toDate(),
  })

  const [activeTab, setActiveTab] = useState<AnalyticsType>('overview')

  const rangeData = {
    start_at: getFormattedUnixDate(range.from, { time: 'startOfDay' }),
    end_at: getFormattedUnixDate(range.to, { time: 'endOfDay' }),
    sort_by: 'spent',
    sort_direction: 'desc',
  }

  const creditAnalyticsParams = {
    request: {
      sort_by: 'balance_summary',
      sort_direction: 'desc',
      page_size: 100,
      page_number: 1,
    },
  }

  const { data: serverAnalytics, isLoading: isServerAnalyticsLoading } =
    useGetOrgServerAnalytics(rangeData)

  const { data: storageAnalytics, isLoading: isStorageAnalyticsLoading } =
    useGetOrgStorageAnalytics(rangeData)

  const { data: creditAnalytics, isLoading: isCreditAnalyticsLoading } =
    useGetOrgCreditAnalytics(creditAnalyticsParams)

  const isActiveLoading =
    activeTab === 'overview'
      ? isCreditAnalyticsLoading
      : activeTab === 'server'
        ? isServerAnalyticsLoading
        : isStorageAnalyticsLoading

  const totalDuration = useMemo(() => {
    if (activeTab === 'overview') return null

    const activeData =
      activeTab === 'server' ? serverAnalytics : storageAnalytics
    if (!activeData?.total_duration_seconds) return null

    const { hours, minutes, seconds } = getTimeData(
      activeData.total_duration_seconds
    )
    return `${hours}h ${minutes}m ${seconds}s`
  }, [activeTab, serverAnalytics, storageAnalytics])

  const totalSpent = useMemo(() => {
    if (activeTab === 'overview') return null

    const activeData =
      activeTab === 'server' ? serverAnalytics : storageAnalytics
    if (!activeData?.total_spent) return null
    return formatCurrency(activeData.total_spent / 100)
  }, [activeTab, serverAnalytics, storageAnalytics])

  const onTabChange = (value: string) => setActiveTab(value as AnalyticsType)

  return (
    <div>
      <div className="mb-4">
        <h3 className="text-xl font-semibold">Resource Consumption</h3>
      </div>

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <Tabs
          value={activeTab}
          onValueChange={onTabChange}
          className="w-[400px]"
        >
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="server">Server</TabsTrigger>
            <TabsTrigger value="storage">Storage</TabsTrigger>
          </TabsList>
        </Tabs>

        {activeTab !== 'overview' && (
          <CalendarPopover
            range={range}
            onSelect={setRange}
            shouldDisable={(date: Date) =>
              isOrgLoading || dayjs(date).isBefore(dayjs(org?.org.created_at))
            }
          />
        )}
      </div>

      <div className="rounded-lg overflow-hidden">
        {isActiveLoading ? (
          <div className="flex justify-center items-center h-[300px]">
            <LoadingMessage
              message={`Loading ${activeTab} analytics...`}
              centered
            />
          </div>
        ) : (
          <div className="flex flex-col">
            {activeTab === 'overview' ? (
              <AnalyticsOverviewTable data={creditAnalytics?.items || []} />
            ) : (
              (() => {
                const activeData =
                  activeTab === 'server' ? serverAnalytics : storageAnalytics
                const activeItems = activeData?.items || []
                return (
                  <>
                    <AnalyticsUsersTable data={activeItems} />
                    {activeItems.length > 0 && (
                      <div className="bg-muted/20 border shadow-sm rounded-b-lg -mt-px overflow-hidden">
                        <Table className="rounded-b-lg overflow-hidden">
                          <TableFooter>
                            <TableRow className="rounded-b-lg overflow-hidden">
                              <TableCell className="py-4 pl-12 font-semibold w-[45%] rounded-bl-lg">
                                Total Account Usage
                              </TableCell>
                              <TableCell className="py-4 pl-4 font-semibold w-[27.5%]">
                                {totalDuration || '0h 0m 0s'}
                              </TableCell>
                              <TableCell className="py-4 pl-4 font-semibold w-[27.5%] rounded-br-lg">
                                {totalSpent || formatCurrency(0)}
                              </TableCell>
                            </TableRow>
                          </TableFooter>
                        </Table>
                      </div>
                    )}
                  </>
                )
              })()
            )}
          </div>
        )}
      </div>
    </div>
  )
}
